import { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { Button } from "@/components/ui/button";
import { Download, Copy, ExternalLink, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const DOT_STYLES = [
  { value: "square", label: "Carré (classique)" },
  { value: "rounded", label: "Arrondi" },
  { value: "dots", label: "Points ronds" },
  { value: "classy", label: "Élégant" },
  { value: "classy-rounded", label: "Élégant arrondi" },
  { value: "extra-rounded", label: "Extra arrondi" },
];

const CORNER_STYLES = [
  { value: "square", label: "Carrés" },
  { value: "extra-rounded", label: "Arrondis" },
  { value: "dot", label: "Ronds" },
];

const buildOptions = ({ url, size, qrShape, dotStyle, cornerStyle, logo }) => ({
  width: size,
  height: size,
  type: "svg",
  shape: qrShape,
  data: url,
  margin: qrShape === "circle" ? 2 : 8,
  qrOptions: { errorCorrectionLevel: "H" },
  image: logo || undefined,
  imageOptions: { margin: 4, imageSize: 0.32, hideBackgroundDots: true, crossOrigin: "anonymous" },
  dotsOptions: { type: dotStyle, color: "#0A0A0A" },
  backgroundOptions: { color: "#FFFFFF" },
  cornersSquareOptions: { type: cornerStyle, color: "#0A0A0A" },
  cornersDotOptions: { type: cornerStyle === "dot" ? "dot" : "square", color: "#0A0A0A" },
});

export default function QRCodeCard({
  url,
  title = "QR Code",
  subtitle = "",
  fileBase = "qr",
  size = 200,
  primaryColor = "#0066FF",
}) {
  const previewRef = useRef(null);
  const qrRef = useRef(null);
  const fileInputRef = useRef(null);
  const [qrShape, setQrShape] = useState("square");
  const [dotStyle, setDotStyle] = useState("square");
  const [cornerStyle, setCornerStyle] = useState("square");
  const [logo, setLogo] = useState("");

  useEffect(() => {
    if (!url || !previewRef.current) return;
    const opts = buildOptions({ url, size, qrShape, dotStyle, cornerStyle, logo });
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(opts);
      qrRef.current.append(previewRef.current);
    } else {
      qrRef.current.update(opts);
    }
  }, [url, size, qrShape, dotStyle, cornerStyle, logo]);

  if (!url) return null;

  const downloadAs = async (extension) => {
    const hiRes = new QRCodeStyling(
      buildOptions({ url, size: 1024, qrShape, dotStyle, cornerStyle, logo })
    );
    await hiRes.download({ name: fileBase, extension });
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Choisissez une image (PNG, JPG…)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const selectCls =
    "w-full bg-white/[0.04] border border-white/10 text-zinc-300 text-xs rounded-md h-9 px-2 outline-none focus:border-white/30";

  return (
    <div
      className="bg-[#111216] border border-white/10 rounded-xl p-5 md:p-6"
      data-testid="qr-card"
    >
      <div className="mb-4">
        <div className="text-overline mb-1" style={{ color: primaryColor }}>
          QR Code · Trophée
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
          {title}
        </h3>
        {subtitle && <p className="text-zinc-500 text-xs font-body mt-1">{subtitle}</p>}
        <p className="text-zinc-500 text-xs font-body mt-1 break-all">{url}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-5 items-center md:items-start">
        <div
          className="bg-white p-3 rounded-lg shrink-0 shadow-[0_0_24px_rgba(0,0,0,0.4)]"
          ref={previewRef}
          data-testid="qr-canvas-wrap"
        />

        <div className="flex-1 w-full space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-body block mb-1">
              Forme globale
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="qr-shape-square"
                onClick={() => setQrShape("square")}
                className={`h-9 rounded-md text-xs font-body border transition-colors ${
                  qrShape === "square"
                    ? "border-white/50 bg-white/10 text-white"
                    : "border-white/10 bg-white/[0.04] text-zinc-400 hover:border-white/30"
                }`}
              >
                Carré
              </button>
              <button
                type="button"
                data-testid="qr-shape-circle"
                onClick={() => setQrShape("circle")}
                className={`h-9 rounded-md text-xs font-body border transition-colors ${
                  qrShape === "circle"
                    ? "border-white/50 bg-white/10 text-white"
                    : "border-white/10 bg-white/[0.04] text-zinc-400 hover:border-white/30"
                }`}
              >
                Rond
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-body block mb-1">
                Forme des points
              </label>
              <select
                data-testid="qr-dot-style"
                value={dotStyle}
                onChange={(e) => setDotStyle(e.target.value)}
                className={selectCls}
              >
                {DOT_STYLES.map((s) => (
                  <option key={s.value} value={s.value} className="bg-[#111216]">
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-body block mb-1">
                Coins
              </label>
              <select
                data-testid="qr-corner-style"
                value={cornerStyle}
                onChange={(e) => setCornerStyle(e.target.value)}
                className={selectCls}
              >
                {CORNER_STYLES.map((s) => (
                  <option key={s.value} value={s.value} className="bg-[#111216]">
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="qr-logo-input"
            onChange={onLogoFile}
          />
          {!logo ? (
            <Button
              type="button"
              variant="outline"
              data-testid="qr-add-logo"
              onClick={() => fileInputRef.current?.click()}
              className="w-full bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white rounded-md h-10"
            >
              <ImagePlus className="w-4 h-4 mr-2" /> Ajouter un logo au centre
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="Logo QR"
                className="w-9 h-9 rounded object-contain bg-white/10 border border-white/10 p-0.5"
              />
              <span className="text-xs text-zinc-400 font-body flex-1">Logo intégré au centre</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="qr-remove-logo"
                onClick={() => setLogo("")}
                className="h-8 px-2 text-zinc-400 hover:text-white hover:bg-white/5"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <Button
              type="button"
              data-testid="qr-download-png"
              onClick={() => downloadAs("png")}
              className="w-full text-white rounded-md h-10 font-body font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              <Download className="w-4 h-4 mr-2" /> PNG (impression)
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="qr-download-svg"
              onClick={() => downloadAs("svg")}
              className="w-full bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white rounded-md h-10"
            >
              <Download className="w-4 h-4 mr-2" /> SVG (gravure laser)
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="ghost"
                data-testid="qr-copy-link"
                onClick={copyLink}
                className="bg-transparent border border-white/10 hover:bg-white/5 text-zinc-300 hover:text-white rounded-md h-10"
              >
                <Copy className="w-4 h-4 mr-2" /> Copier
              </Button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid="qr-open-link"
                className="inline-flex items-center justify-center bg-transparent border border-white/10 hover:bg-white/5 text-zinc-300 hover:text-white rounded-md h-10 text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Ouvrir
              </a>
            </div>
            <p className="text-[11px] text-zinc-500 font-body leading-relaxed pt-2">
              Correction d'erreur H (30%) — reste lisible même partiellement
              abîmé après gravure, y compris avec un logo au centre.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
